// Live-refresh + manual-takeover decisions for the AI threads view.
// Conversations continue server-side (sender texts, AI replies, owner takes over)
// with nothing pushing updates to the dashboard — so the whole Chat tab polls
// history while visible. Not just the selected session: inbound texts update any
// thread, create brand-new ones, and must show in the "All sessions" view too.

export const POLL_MS = 8000;

export function isExternalSession(session) {
  return !!(session && (session.channel === 'sms' || session.channel === 'voice' || session.channel === 'whatsapp'));
}

// Landing behavior: an unread external thread wins over the "New session" default —
// but ONLY when it is the most recent thread overall. An older unread thread the
// user keeps skipping past is deliberately ignored, not "new"; anything with newer
// activity (including the user's own web chats) means we stay on the default.
// getLastSeen(sessionId) → ms epoch or null; null counts as read (first-visit keys
// are seeded to the latest message by loadHistory).
export function pickUnreadSession(sessions, getLastSeen) {
  var lastTsOf = function(s) {
    var msgs = s.messages || [];
    var last = msgs.length ? msgs[msgs.length - 1] : null;
    return Date.parse(s.endTime || (last && last.timestamp) || '') || 0;
  };
  var newest = null, newestTs = 0;
  (sessions || []).forEach(function(s) {
    // Same exclusions the session list applies — hidden sessions can't outrank.
    if (!s || s.channel === 'task' || /^(test-|tA-|tB-)/.test(s.sessionId || '')) return;
    var ts = lastTsOf(s);
    if (ts > newestTs) { newestTs = ts; newest = s; }
  });
  if (!newest || !isExternalSession(newest)) return null;
  var seen = getLastSeen(newest.sessionId);
  if (seen == null || newestTs <= seen) return null;
  return newest.sessionId;
}

export function shouldPollHistory(activeTab, sending, visibilityState) {
  if (activeTab !== 'Chat') return false;
  if (sending) return false;
  if (visibilityState && visibilityState !== 'visible') return false;
  return true;
}

// Override mode: a manually-taken-over thread is owner↔sender — the send button
// must default to SMS. Everything else defaults to the AI.
export function defaultSendTarget(paused) {
  return paused ? 'sms' : 'ai';
}

// Cheap change-detector for polled history. A quiet poll may only re-render when
// this differs — otherwise React state churn re-arms the auto-follow scroll lock
// every 8s and fights the user's finger. Captures what the view renders: session
// set/order, message count, last-message timestamp, pause flag, title.
export function sessionsFingerprint(sessions) {
  return (sessions || []).map(function(s) {
    var msgs = s.messages || [];
    var last = msgs.length ? msgs[msgs.length - 1] : null;
    return [s.sessionId, msgs.length, last && last.timestamp, s.aiPaused ? 1 : 0, s.title || ''].join('|');
  }).join('~');
}

// The "AI paused" chip: only SMS sessions the server flagged as manually taken over.
export function isPausedSession(session) {
  return !!(session && session.aiPaused && session.channel === 'sms');
}

// Payload for POST /api/aiassistant/thread-resume. Null number = clear the pause on
// every one of the user's numbers for this caller.
export function resumePayload(session) {
  if (!session || !session.caller) return null;
  return { caller: session.caller, number: session.servedNumber || null };
}
